import { Box, SxProps, Theme } from '@mui/material';
import { JSX, Ref } from 'react';
import { toTimeInputValue } from '../../library/dateTimeHelper';
import { AppDatePicker } from './AppDatePicker';
import { AppTimePicker } from './AppTimePicker';
import { dateTimeRowStyles, dateTimeDateStyles, dateTimeTimeStyles } from './appPickers.styles';

export interface AppDateTimePickerProps {
    label: string;
    /** The time half's own label, since the two controls are now separate fields. */
    timeLabel: string;
    /** A local wall clock (`2026-01-21T13:00`). The API's `:00` suffix is accepted. */
    value: string;
    onChange: (value: string) => void;
    onBlur?: () => void;
    name?: string;
    inputRef?: Ref<HTMLInputElement>;
    disabled?: boolean;
    required?: boolean;
    error?: boolean;
    helperText?: string;
    /** The earliest day the date half offers, as `YYYY-MM-DD`. */
    minDate?: string;
    maxDate?: string;
    disableFuture?: boolean;
    minutesStep?: number;
    /** The exact times to offer, for a caller that has asked which of them are free. */
    timeOptions?: string[];
    /** Selection only on the time half: nothing off `timeOptions` may be chosen or typed. */
    strictTime?: boolean;
    /** What the time popup says when `timeOptions` is empty. */
    noTimesText?: string;
    sx?: SxProps<Theme>;
}

/** `2026-01-21T13:00:00` as its two halves, either of which may be empty. */
const splitWallClock = (value: string): { date: string; time: string } => ({
    date: value.slice(0, 10),
    time: toTimeInputValue(value.slice(11)),
});

/**
 * A day and an hour, as two controls rather than one.
 *
 * It used to be a single `DateTimePicker`, and that was the wrong shape twice over. Its
 * popup carried a calendar *and* an hour column *and* a minute column, which on a phone-
 * sized dialog rendered wider than the dialog itself and covered its own buttons. And it
 * asked for one answer where there are plainly two: people pick a day, then a time.
 *
 * Smashing Magazine's survey of date and time pickers reaches the same conclusion -- "the
 * easiest way to achieve time selection would be by providing an extra input field as a
 * step after the date has been selected" -- and it costs nothing here, because both halves
 * already exist as components in their own right.
 *
 * The value is still one wall-clock string in and out, so no caller learned that the field
 * became two. Seconds are dropped on the way out, which is what `toDateTimeInputValue`
 * always did and what keeps `addMinutes` and the create-appointment request unchanged.
 *
 * Deliberately unbounded by default. `timeOptions` and `strictTime` narrow the hour half for
 * a caller that has asked the API which windows are free -- which the therapist's diary now
 * does. That is not the old bound coming back: a therapist may still book whenever they need
 * to, because the list they are offered is the whole day rather than the clinic's opening
 * hours. What it excludes is time somebody is already in.
 */
export const AppDateTimePicker = (props: AppDateTimePickerProps): JSX.Element => {
    const { date, time } = splitWallClock(props.value);

    /*
     * Half a value is not a value. Until both halves are answered the field reports empty,
     * so a schema saying `required` still refuses a day with no hour on it -- rather than
     * letting `2026-01-21T` through as a truthy string.
     */
    const emit = (nextDate: string, nextTime: string): void =>
        props.onChange(nextDate && nextTime ? `${nextDate}T${nextTime}` : '');

    /*
     * Merged, never replaced. A caller passes `sx` to place the pair on the page -- the
     * dialogs pass their own `{ mt: 2 }` -- and `props.sx ?? dateTimeRowStyles` would let
     * that spacing delete `display: flex` and the gap with it, leaving the two fields
     * stacked flush with each label sitting on the box above it.
     */
    const layout: SxProps<Theme> = [
        dateTimeRowStyles,
        ...(Array.isArray(props.sx) ? props.sx : [props.sx]),
    ] as SxProps<Theme>;

    return (
        <Box sx={layout}>
            <AppDatePicker
                label={props.label}
                value={date}
                onChange={(next: string) => emit(next, time)}
                onBlur={props.onBlur}
                name={props.name}
                inputRef={props.inputRef}
                disabled={props.disabled}
                required={props.required}
                error={props.error}
                // Carried by the time half, so one message sits under the pair rather than
                // being repeated under each.
                minDate={props.minDate}
                maxDate={props.maxDate}
                disableFuture={props.disableFuture}
                fullWidth
                sx={dateTimeDateStyles}
            />

            <AppTimePicker
                label={props.timeLabel}
                value={time}
                onChange={(next: string) => emit(date, next)}
                onBlur={props.onBlur}
                disabled={props.disabled}
                required={props.required}
                error={props.error}
                helperText={props.helperText}
                minutesStep={props.minutesStep}
                options={props.timeOptions}
                strict={props.strictTime}
                noOptionsText={props.noTimesText}
                fullWidth
                sx={dateTimeTimeStyles}
            />
        </Box>
    );
};
